// Essential utility functions for the Airbolt SDK
import type { FetchOptions, RequestHeaders } from "./types";
import {
  AirboltError,
  AirboltNetworkError,
  createErrorFromResponse,
  createNetworkError,
  isRetryableError,
} from "./errors";

// Type declaration for PostHog on window object
declare global {
  interface Window {
    posthog?: {
      captureException: (
        error: Error,
        properties?: Record<string, unknown>,
      ) => void;
    };
  }
}

/**
 * Creates standardized headers for API requests
 */
export function createHeaders(
  projectId: string,
  userId?: string,
): RequestHeaders {
  const headers: RequestHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${projectId}`,
  };

  if (userId) {
    headers["x-end-user-id"] = userId;
  }

  return headers;
}

/**
 * Network requests with exponential backoff retry logic
 */
export async function fetchWithRetry(
  url: string,
  options: RequestInit & { fetchOptions?: FetchOptions } = {},
): Promise<Response> {
  const { fetchOptions = {}, ...fetchOptions_ } = options;
  const {
    timeout = 30000,
    retries = 3,
    retryDelay = 1000,
    maxRetryDelay = 10000,
  } = fetchOptions;

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      // Create timeout controller
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      // Handle external abort signal
      let abortListener: (() => void) | null = null;
      if (options.signal) {
        if (options.signal.aborted) {
          controller.abort();
        } else {
          abortListener = () => controller.abort();
          options.signal.addEventListener("abort", abortListener);
        }
      }

      let response: Response;
      try {
        response = await fetch(url, {
          ...fetchOptions_,
          signal: controller.signal,
        });
      } finally {
        // Always clear timeout to prevent memory leaks
        clearTimeout(timeoutId);

        // Remove abort listener to prevent accumulation
        if (abortListener && options.signal) {
          options.signal.removeEventListener("abort", abortListener);
        }
      }

      if (!response.ok) {
        const responseText = await response.text().catch(() => "");
        const error = createErrorFromResponse(response, responseText);

        // Retry on server errors
        if (
          response.status >= 500 &&
          isRetryableError(error) &&
          attempt < retries
        ) {
          lastError = error;
          const delay = Math.min(
            retryDelay * Math.pow(2, attempt),
            maxRetryDelay,
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        throw error;
      }

      return response;
    } catch (error) {
      // If error is already an AirboltError, preserve it
      if (error instanceof AirboltError) {
        lastError = error;
        if (isRetryableError(error) && attempt < retries) {
          const delay = Math.min(
            retryDelay * Math.pow(2, attempt),
            maxRetryDelay,
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        // Capture final error for debugging
        if (typeof window !== "undefined" && window.posthog) {
          try {
            window.posthog.captureException(error, {
              sdk_component: "fetchWithRetry",
              fetch_url: url,
              fetch_method: fetchOptions_.method || "GET",
              error_code: error.code,
              attempt: attempt + 1,
              max_retries: retries,
            });
          } catch {
            // Silently ignore PostHog errors
          }
        }

        throw error;
      }

      // Only convert unknown errors to network errors
      const networkError =
        error instanceof Error
          ? createNetworkError(error)
          : new AirboltNetworkError("Unknown error occurred");

      lastError = networkError;

      if (isRetryableError(networkError) && attempt < retries) {
        const delay = Math.min(
          retryDelay * Math.pow(2, attempt),
          maxRetryDelay,
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      // Capture final network error for debugging
      if (typeof window !== "undefined" && window.posthog) {
        try {
          window.posthog.captureException(networkError, {
            sdk_component: "fetchWithRetry",
            fetch_url: url,
            fetch_method: fetchOptions_.method || "GET",
            attempt: attempt + 1,
            max_retries: retries,
          });
        } catch {
          // Silently ignore PostHog errors
        }
      }

      throw networkError;
    }
  }

  throw lastError || new AirboltNetworkError("Request failed after retries");
}

/**
 * Security critical validation for project ID format
 */
export function validateProjectId(projectId: string): boolean {
  return /^proj_[a-zA-Z0-9]+$/.test(projectId);
}

/**
 * Safe JSON parsing with error handling
 */
export function safeJsonParse<T = any>(text: string): T | null {
  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

/**
 * Debug logging utility
 */
export function debugLog(debug: boolean, message: string, data?: any): void {
  if (!debug) return;

  const timestamp = new Date().toISOString();
  const prefix = `[airbolt-sdk ${timestamp}]`;

  if (data !== undefined) {
    console.log(prefix, message, data);
  } else {
    console.log(prefix, message);
  }
}

/**
 * Get the default base URL for the Airbolt API
 * Production default: https://api.airbolt.ai
 *
 * Can be overridden via environment variables:
 * - AIRBOLT_API_URL (Node.js environments)
 * - NEXT_PUBLIC_AIRBOLT_API_URL (Next.js client-side)
 */
export function getDefaultBaseUrl(): string {
  // Check for environment variable override (for testing/development)
  if (typeof process !== "undefined" && process.env) {
    // Priority: AIRBOLT_API_URL first, then NEXT_PUBLIC_AIRBOLT_API_URL
    if (process.env["AIRBOLT_API_URL"]) {
      return process.env["AIRBOLT_API_URL"];
    }
    if (process.env["NEXT_PUBLIC_AIRBOLT_API_URL"]) {
      return process.env["NEXT_PUBLIC_AIRBOLT_API_URL"];
    }
  }

  // Production default
  return "https://www.airbolt.ai";
}
